import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Coffee, Lock, LogIn, LogOut } from 'lucide-react';
import { Button, ClockDigits, Ring, Skeleton, cn } from '@hrms/ui';
import { canAnywhere, type AttendanceStatusDto } from '@hrms/shared';
import { formatTimeInZone } from '../../lib/datetime.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { minutesLate } from './arrival.js';
import { useAttendanceStatus } from './useToday.js';

const LIVE_STATUS_LABEL = {
  clocked_in: 'In office',
  clocked_out: 'Clocked out',
  on_break: 'On break',
  not_started: 'Not clocked in',
  on_leave: 'On leave',
  weekly_off: 'Weekly off',
  holiday: 'Holiday',
} as const;

type LiveStatus = keyof typeof LIVE_STATUS_LABEL;

/** The status chip's tone — the same colour per state the attendance page uses, so the two never
 *  disagree (P3-6). The dot pulses only while the day is running (working or on break). */
const STATUS_TONE: Record<LiveStatus, { chip: string; dot: string; pulse: boolean }> = {
  clocked_in: { chip: 'bg-success-subtle text-success', dot: 'bg-success', pulse: true },
  on_break: { chip: 'bg-warning-subtle text-warning', dot: 'bg-warning', pulse: true },
  clocked_out: { chip: 'bg-bg-subtle text-fg-muted', dot: 'bg-fg-subtle', pulse: false },
  not_started: { chip: 'bg-bg-subtle text-fg-muted', dot: 'bg-fg-subtle', pulse: false },
  on_leave: { chip: 'bg-accent-subtle text-accent', dot: 'bg-accent', pulse: false },
  weekly_off: { chip: 'bg-accent-subtle text-accent', dot: 'bg-accent', pulse: false },
  holiday: { chip: 'bg-accent-subtle text-accent', dot: 'bg-accent', pulse: false },
};

/** Why the punch button isn't offered — shown in its place instead of a greyed-out button. A
 *  weekly off, holiday or leave day can still be punched (that is how comp-off is earned, P3-6),
 *  so only a locked day or one already closed with a clock-out rests here. */
function restingState(status: AttendanceStatusDto | undefined, mayPunch: boolean, readOnly: boolean) {
  if (!mayPunch) {
    return { icon: Lock, text: readOnly ? 'Workspace is read-only' : 'Punching is not available to you' };
  }
  if (status?.isLocked) return { icon: Lock, text: 'Attendance is locked for today' };
  if (status?.punchState === 'clocked_out') return { icon: LogOut, text: 'Clocked out for today' };
  return { icon: Lock, text: 'Punching is unavailable right now' };
}

function clock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/**
 * Today's attendance. The punch itself (geo + selfie capture) lives on the attendance page, so the
 * button here hands off to it rather than duplicating that flow — Home only reads state.
 */
export function AttendanceClockCard() {
  const navigate = useNavigate();
  const { actor } = useAuthenticatedSession();
  const query = useAttendanceStatus();
  const status = query.data?.body;
  const [now, setNow] = useState(() => new Date());

  const clockedIn = status?.punchState === 'clocked_in';
  const onBreak = status?.punchState === 'on_break';
  const clockedOut = status?.punchState === 'clocked_out';
  useEffect(() => {
    if (!clockedIn) return;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [clockedIn]);

  if (query.isPending) return <Skeleton className="h-64 w-full rounded-xl" />;

  const shift = status?.shift ?? null;
  // The server's worked minutes already leave out breaks; only the time since it answered is added live, and only
  // while working. Counting from the first clock-in, as this card used to, counted breaks as work (P3-6).
  const workedSeconds =
    (status?.totalWorkedMinutes ?? 0) * 60 +
    (status && clockedIn ? Math.max(0, (now.getTime() - new Date(status.currentTime).getTime()) / 1000) : 0);
  // The same rule the attendance engine applies (packages/attendance calc/hours.ts): overtime is
  // whatever is worked beyond the shift's full-day threshold.
  const goalMinutes = (shift?.fullDayThresholdHours ?? 0) * 60;
  const percent =
    goalMinutes > 0 ? Math.min(100, Math.round((workedSeconds / 60 / goalMinutes) * 100)) : 0;
  const overtimeMinutes =
    goalMinutes > 0 ? Math.max(0, Math.floor(workedSeconds / 60 - goalMinutes)) : 0;
  // The attendance page's rule (P3-6): a punch is allowed on any unlocked day not already closed with a clock-out,
  // a weekly off or holiday included (that is how comp-off is earned).
  // Also needs the permission: a read-only workspace (ended trial) loses it, and the button would only lead
  // to a refused punch.
  const mayPunch = canAnywhere(actor, 'attendance:punch');
  const canPunch = status ? mayPunch && !status.isLocked && !clockedOut : false;

  const arrival = arrivalOf(status);
  const tone = STATUS_TONE[status?.liveStatus ?? 'not_started'];
  const resting = restingState(status, mayPunch, Boolean(actor.readOnly));
  const RestingIcon = resting.icon;

  return (
    <section className="rounded-2xl border border-border bg-surface p-5 shadow-xs">
      <header className="mb-5 flex flex-wrap items-center gap-2">
        <h2 className="font-display text-base font-bold text-fg">Today&apos;s Attendance</h2>
        {status && (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold',
              tone.chip,
            )}
          >
            <span className="relative flex size-1.5" aria-hidden="true">
              {tone.pulse && (
                <span
                  className={cn(
                    'absolute inline-flex size-full rounded-full opacity-60 motion-safe:animate-ping',
                    tone.dot,
                  )}
                />
              )}
              <span className={cn('relative inline-flex size-1.5 rounded-full', tone.dot)} />
            </span>
            {LIVE_STATUS_LABEL[status.liveStatus]}
            {shift ? ` · ${shift.name}` : ''}
          </span>
        )}
        {shift && (
          <span className="ml-auto rounded-lg border border-border bg-bg-subtle px-2.5 py-1 text-xs font-medium tabular-nums text-fg-muted">
            {shift.startTime} – {shift.endTime}
          </span>
        )}
      </header>

      <div className="flex flex-col items-center gap-6 sm:flex-row">
        <div className="shrink-0">
          <Ring
            value={percent}
            size={164}
            strokeWidth={10}
            color={onBreak ? 'var(--warning)' : clockedIn ? 'var(--success)' : 'var(--accent)'}
            label={onBreak ? 'On break' : 'Working time'}
            valueText={
              <ClockDigits
                value={clock(workedSeconds)}
                live={clockedIn}
                minorSeconds
                className="text-[1.65rem]"
              />
            }
            sublabel={shift ? `${percent}% of ${shift.fullDayThresholdHours}h goal` : undefined}
            sublabelTone={percent > 0 ? 'success' : 'muted'}
          />
        </div>

        <div className="flex w-full min-w-0 flex-1 flex-col justify-between gap-5">
          {canPunch && onBreak ? (
            <Button
              variant="secondary"
              size="lg"
              className="h-11 w-full gap-2 rounded-xl px-6 font-semibold sm:w-auto sm:self-start"
              onClick={() => navigate('/attendance')}
            >
              <Coffee className="size-4 text-warning" />
              End break
            </Button>
          ) : canPunch ? (
            <Button
              variant="primary"
              size="lg"
              className="group h-11 w-full gap-2 rounded-xl px-6 font-semibold shadow-accent transition-all duration-200 hover:-translate-y-px active:translate-y-0 sm:w-auto sm:self-start"
              onClick={() => navigate('/attendance')}
            >
              {clockedIn ? <LogOut className="size-4" /> : <LogIn className="size-4" />}
              {clockedIn ? 'Clock out' : 'Clock in'}
              <ArrowRight
                className="size-4 opacity-70 transition-transform duration-200 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Button>
          ) : (
            <p className="flex items-center gap-2.5 rounded-xl border border-dashed border-border bg-bg-subtle/60 px-4 py-3 text-sm font-medium text-fg-muted">
              <RestingIcon className="size-4 shrink-0 text-accent" aria-hidden="true" />
              {resting.text}
            </p>
          )}

          <dl className="grid grid-cols-3 gap-2">
            <Metric
              label="Arrival"
              value={arrival.value}
              subtext={arrival.subtext}
              subtextColor={
                !status?.firstIn ? 'text-fg-subtle' : arrival.late ? 'text-warning' : 'text-success'
              }
            />
            <Metric
              label="Overtime"
              value={goalMinutes > 0 ? formatHm(overtimeMinutes) : '—'}
              subtext={goalMinutes > 0 && overtimeMinutes > 0 ? 'Beyond shift goal' : undefined}
            />
            <Metric
              label="Break"
              value={status ? `${status.totalBreakMinutes} min` : '—'}
              subtext={shift ? `of ${shift.breakMinutes} min allowed` : 'Taken today'}
              subtextColor="text-fg-subtle"
            />
          </dl>
        </div>
      </div>
    </section>
  );
}

/** "05h 30m" — a duration in whole minutes. */
function formatHm(totalMinutes: number): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(totalMinutes / 60))}h ${pad(totalMinutes % 60)}m`;
}

/** Minutes since midnight of `iso` as a wall clock in `timeZone` (the employee's, not the viewer's). */
function minutesOfDayInZone(iso: string, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).formatToParts(new Date(iso));
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value);
    return Number.isFinite(hour) && Number.isFinite(minute) ? hour * 60 + minute : null;
  } catch {
    return null;
  }
}

/** First punch-in time, and whether it was inside the shift's start + grace window. */
function arrivalOf(status: AttendanceStatusDto | undefined): {
  value: string;
  subtext?: string;
  late: boolean;
} {
  if (!status?.firstIn) return { value: '—', subtext: 'Not clocked in', late: false };
  const value = formatTimeInZone(status.firstIn, status.timezone);
  const shift = status.shift;
  const arrived = minutesOfDayInZone(status.firstIn, status.timezone);
  if (!shift || arrived === null) return { value, late: false };
  const [h, m] = shift.startTime.split(':').map(Number);
  if (h === undefined || m === undefined) return { value, late: false };
  const lateBy = minutesLate(arrived, h * 60 + m, shift.graceMinutes);
  return lateBy > 0
    ? { value, subtext: `Late by ${lateBy} min`, late: true }
    : { value, subtext: 'On time', late: false };
}

function Metric({
  label,
  value,
  subtext,
  subtextColor = 'text-success',
}: {
  label: string;
  value: string;
  subtext?: string;
  subtextColor?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl bg-bg-subtle/70 px-3 py-2.5 text-left">
      <dt className="truncate text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
        {label}
      </dt>
      <dd className="mt-1 truncate font-display text-sm font-bold tabular-nums text-fg">{value}</dd>
      {subtext && (
        <p className={cn('mt-0.5 truncate text-[10px] font-semibold', subtextColor)}>{subtext}</p>
      )}
    </div>
  );
}
